import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type SubmittedReview = {
  rating: number;
  title?: string | null;
  body: string;
  reviewerName?: string | null;
  verifiedPurchase?: boolean;
  reviewDate?: string | null;
  amazonReviewId?: string;
  reviewUrl?: string | null;
};

type Scrape = {
  id: string;
  asin: string;
  amazonUrl: string;
  productTitle: string | null;
  brandName: string | null;
  customerId: string | null;
  source: "auto" | "human";
  status: "pending" | "running" | "submitted" | "succeeded" | "failed" | "rejected";
  coverage: string | null;
  errorCategory: string | null;
  errorMessage: string | null;
  manualScraper: { id: string; email: string; name: string | null } | null;
  claimedAt: string | null;
  submittedAt: string | null;
  qaedAt: string | null;
  qaedBy: string | null;
  rejectionReason: string | null;
  submissionData: {
    reviews?: SubmittedReview[];
    notes?: string;
    noReviewsReason?: string;
  } | null;
};

const noReviewsLabels: Record<string, string> = {
  no_negative_reviews: "No negative reviews found",
  no_reviews: "No reviews found",
  no_matching_star_filter: "No reviews matching requested star filter",
  amazon_page_unavailable: "Amazon page unavailable",
  duplicate_reviews_saved_elsewhere: "Reviews already saved to another product / duplicate",
  product_not_associated_with_brand: "Product not associated with brand",
};

function AdminScrapeDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [scrape, setScrape] = useState<Scrape | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  async function refresh() {
    const res = await api<{ scrape: Scrape }>(`/admin/scrapes/${id}`);
    if (res.ok && res.data) setScrape(res.data.scrape);
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
  }, [id]);

  async function approve() {
    setError(null);
    setActing(true);
    const res = await api<{
      reviewCount: number;
      auditId: string | null;
      nextScrape: { id: string } | null;
    }>(
      `/admin/scrapes/${id}/approve`,
      { method: "POST", body: JSON.stringify({}) }
    );
    setActing(false);
    if (!res.ok) {
      setError(res.error ?? "Approve failed.");
      return;
    }
    if (res.data?.nextScrape) {
      navigate(`/admin/scrapes/${res.data.nextScrape.id}`, { replace: true });
      return;
    }
    navigate("/admin/scrapes?status=submitted", { replace: true });
  }

  async function reject() {
    if (!rejectReason.trim()) {
      setError("Reason is required.");
      return;
    }
    setError(null);
    setActing(true);
    const res = await api(`/admin/scrapes/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason: rejectReason.trim() }),
    });
    setActing(false);
    if (!res.ok) {
      setError(res.error ?? "Reject failed.");
      return;
    }
    void refresh();
  }

  async function queueMoreReviews() {
    setError(null);
    setActing(true);
    const res = await api<{ scrape: { id: string } }>(
      `/admin/scrapes/${id}/queue-more`,
      { method: "POST", body: JSON.stringify({}) }
    );
    setActing(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Could not queue another scrape job.");
      return;
    }
    navigate(`/admin/scrapes/${res.data.scrape.id}`);
  }

  if (loading) {
    return (
      <AdminLayout>
        <p className="text-sm text-slate-500">Loading…</p>
      </AdminLayout>
    );
  }
  if (!scrape) {
    return (
      <AdminLayout>
        <p className="text-sm text-red-600">Not found.</p>
        <button
          type="button"
          onClick={() => navigate("/admin/scrapes")}
          className="mt-3 text-xs text-slate-700 underline-offset-4 hover:underline"
        >
          ← Back to scrape queue
        </button>
      </AdminLayout>
    );
  }

  const reviews = scrape.submissionData?.reviews ?? [];
  const noReviewsReason = scrape.submissionData?.noReviewsReason;
  const submissionNotes = scrape.submissionData?.notes;
  const isZeroReviewSubmission = reviews.length === 0;
  const noReviewsLabel = noReviewsReason
    ? (noReviewsLabels[noReviewsReason] ?? noReviewsReason)
    : "No reviews submitted";
  const approveLabel = isZeroReviewSubmission
    ? "Confirm 0-review outcome"
    : `Approve (${reviews.length} reviews)`;

  return (
    <AdminLayout>
      <Link
        to="/admin/scrapes"
        className="text-xs text-slate-500 hover:text-slate-900"
      >
        ← Back to scrape queue
      </Link>

      <div className="mt-3 flex items-baseline gap-3">
        <h1 className="font-mono text-xl">{scrape.asin}</h1>
        <span className="text-sm text-slate-600">{scrape.brandName || "—"}</span>
      </div>
      {scrape.productTitle && (
        <p className="mt-1 text-sm text-slate-700">{scrape.productTitle}</p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-600">
        <span>Source: <strong>{scrape.source}</strong></span>
        <span>Status: <strong>{scrape.status.replace("_", " ")}</strong></span>
        {scrape.coverage && (
          <span>Coverage: <strong>{scrape.coverage}</strong></span>
        )}
        {scrape.manualScraper && (
          <span>
            Scraper:{" "}
            <strong>{scrape.manualScraper.name || scrape.manualScraper.email}</strong>
          </span>
        )}
        <a
          href={scrape.amazonUrl}
          target="_blank"
          rel="noreferrer"
          className="underline-offset-4 hover:underline"
        >
          View on Amazon
        </a>
      </div>

      {scrape.status === "failed" && (
        <section className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-950">
          <h2 className="text-sm font-semibold">Failure details</h2>
          <dl className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
            <div>
              <dt className="font-medium text-red-700">Category</dt>
              <dd className="mt-1 font-mono text-red-950">
                {scrape.errorCategory || "unknown"}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-red-700">Amazon URL</dt>
              <dd className="mt-1">
                <a
                  href={scrape.amazonUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all underline-offset-4 hover:underline"
                >
                  {scrape.amazonUrl}
                </a>
              </dd>
            </div>
          </dl>
          <pre className="mt-4 max-h-96 overflow-auto whitespace-pre-wrap rounded-xl border border-red-200 bg-white p-3 text-xs text-red-950">
            {scrape.errorMessage || "No error message was stored for this scrape."}
          </pre>
        </section>
      )}

      {scrape.rejectionReason && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          Previous rejection: {scrape.rejectionReason}
        </div>
      )}

      {scrape.status === "submitted" && (
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold">QA decision</h2>
          <p className="mt-1 text-xs text-slate-600">
            {isZeroReviewSubmission
              ? `Confirm to close this scrape as "${noReviewsLabel}" without inserting Review rows or running classification.`
              : `Approve to insert ${reviews.length} Review row${reviews.length === 1 ? "" : "s"} and trigger the classify pipeline.`}{" "}
            Reject sends the assignment back to the manual scraper with a reason.
          </p>
          <div className="mt-3 flex flex-col gap-3">
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={approve}
                disabled={acting}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {acting ? "Working…" : approveLabel}
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection (e.g. Missing fields)"
                className="flex-1 min-w-[240px] rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={reject}
                disabled={acting || !rejectReason.trim()}
              >
                Reject
              </Button>
            </div>
            {error && (
              <p className="text-xs text-red-600" role="alert">
                {error}
              </p>
            )}
          </div>
        </section>
      )}

      {scrape.status === "succeeded" && (
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold">More reviews</h2>
          <p className="mt-1 text-xs text-slate-600">
            This completed scrape stays locked for QA history. Queue a fresh
            pending assignment if the product needs additional reviews.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Button
              type="button"
              onClick={queueMoreReviews}
              disabled={acting}
              size="sm"
            >
              {acting ? "Queuing…" : "Queue more reviews"}
            </Button>
            {error && (
              <p className="text-xs text-red-600" role="alert">
                {error}
              </p>
            )}
          </div>
        </section>
      )}

      <section className="mt-6">
        <h2 className="text-sm font-semibold">
          Submitted reviews ({reviews.length})
        </h2>
        {reviews.length === 0 ? (
          <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
            <p className="font-medium text-slate-900">
              {noReviewsLabel}
            </p>
            {submissionNotes && (
              <p className="mt-2 whitespace-pre-wrap text-xs text-slate-600">
                {submissionNotes}
              </p>
            )}
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            {reviews.map((r, i) => (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 bg-white p-4"
              >
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>
                    {r.rating}★
                    {r.verifiedPurchase ? " · Verified" : ""}
                    {r.reviewerName ? ` · ${r.reviewerName}` : ""}
                    {r.reviewDate ? ` · ${r.reviewDate}` : ""}
                  </span>
                  {r.amazonReviewId && (
                    <span className="font-mono">{r.amazonReviewId}</span>
                  )}
                  {r.reviewUrl && (
                    <a
                      href={r.reviewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-3 text-slate-500 underline hover:text-slate-900"
                    >
                      Open review
                    </a>
                  )}
                </div>
                {r.title && (
                  <h3 className="mt-1 text-sm font-semibold">{r.title}</h3>
                )}
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">
                  {r.body}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </AdminLayout>
  );
}

export default function AdminScrapeDetail() {
  return (
    <AdminGate>
      <AdminScrapeDetailInner />
    </AdminGate>
  );
}
